import _ from 'lodash'
import React from 'react'
import classNames from 'classnames'
import { Classes, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import apis from 'browser/app/models/apis'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { MobileInputField } from 'browser/mobile/components/input/mobile-input-field'
import { TetherTarget } from 'browser/components/atomic-elements/atoms/tether-target'
import { ComponentsContext } from 'browser/contexts/components/components-context'
import { MobileModal } from 'browser/mobile/elements/modal/modal'
import MobileComponents from '../index'
import { MobileEntityPreviewCard } from '../entity-preview-card/mobile-entity-preview-card'
import { MultiMixinRenderer } from 'shared-libs/components/entity/multi-mixin-renderer'
import { Entity } from 'shared-libs/models/entity'
import { evaluateMappings } from 'shared-libs/models/types/storyboard/storyboard-utils'
import { translateString } from 'shared-libs/helpers/utils'
import { globalTranslationTable } from 'browser/mobile/util/global-translation-table'
import '../../styles/_control.scss'
import './_mobile-entity-search-field.scss'
import '../header/_header.scss'
import { MobileEntitySearchFieldOverlay } from './results-overlay'
import { MobileEntitySearchFieldSubmitResultsOverlay } from './submit-mode-results-overlay'
import {
  DEFAULT_ENTITY_TYPE_NAME,
  DEFAULT_MOBILE_WEB_SCHEMA_PATHS,
  IMobileEntitySearchFieldProps,
  IResolvedSchemaPaths,
  ISelectionPayload,
} from './_utils'
import LabelFieldFactory from 'browser/components/atomic-elements/higher-order-components/label-field-factory'

interface IMobileEntitySearchFieldState {
  entity?: Entity
  isLoading: boolean
  submitSearchFilter: string
  submitRequestedSearchFilter: string
  isCurrentFilterCommitted: boolean
  submitNoResultsCount: number
  isSubmitResultsOpen: boolean
  isSubmitButtonVisible: boolean
}

/**
 * @uiComponent
 */

class MobileEntitySearchFieldImpl extends React.Component<
  IMobileEntitySearchFieldProps,
  IMobileEntitySearchFieldState
> {
  static contextType = ComponentsContext
  declare context: React.ContextType<typeof ComponentsContext>

  private store: any
  private entitySchema: any
  private entityDisplayName: string
  private entityDisplayNamePlural: string
  private submitResultsTetherRef: TetherTarget | undefined
  private hasAutoOpenedSearch: boolean
  private submitButtonShowTimer: ReturnType<typeof setTimeout> | null = null

  public static defaultProps: Partial<IMobileEntitySearchFieldProps> = {
    isEditable: true,
    minQueryLength: 3,
    preloadResults: false,
    searchMode: 'auto',
    itemSchemaPath: DEFAULT_MOBILE_WEB_SCHEMA_PATHS.item,
    helpSchemaPath: DEFAULT_MOBILE_WEB_SCHEMA_PATHS.help,
    inlinePreviewSchemaPath: DEFAULT_MOBILE_WEB_SCHEMA_PATHS.inlinePreview,
    emptyResultsSchemaPath: DEFAULT_MOBILE_WEB_SCHEMA_PATHS.emptyResults,
    isHorizontalLayout: false,
  }

  constructor(props: IMobileEntitySearchFieldProps) {
    super(props)

    this.store = apis.getStore()
    this.entitySchema = {}
    this.entityDisplayName = DEFAULT_ENTITY_TYPE_NAME
    this.entityDisplayNamePlural = DEFAULT_ENTITY_TYPE_NAME
    this.hasAutoOpenedSearch = false
    this.state = {
      entity: props.defaultValue,
      isLoading: false,
      submitSearchFilter: '',
      submitRequestedSearchFilter: '',
      isCurrentFilterCommitted: false,
      submitNoResultsCount: 0,
      isSubmitResultsOpen: false,
      isSubmitButtonVisible: false,
    }
  }

  public async componentDidMount() {
    await this.resolveEntitySchema()

    const { value, defaultValue, autoFocus, searchMode, rawQueryPath, frames } = this.props
    const entityId = _.get(value ?? defaultValue, 'entityId')

    if (entityId) {
      await this.fetchValueEntity(entityId)
    } else if (searchMode === 'submit' && rawQueryPath) {
      const existingRawValue = frames?.getValue?.(rawQueryPath)
      if (!_.isEmpty(existingRawValue)) {
        this.setState({ submitSearchFilter: existingRawValue, isCurrentFilterCommitted: false })
      }
    }

    if (searchMode === 'auto' && autoFocus && !this.hasAutoOpenedSearch) {
      this.hasAutoOpenedSearch = true
      this.openOverlay()
    }
  }

  public async componentDidUpdate(prevProps: IMobileEntitySearchFieldProps, prevState: IMobileEntitySearchFieldState) {
    const prevEntityType = _.get(prevProps, 'query.entityType')
    const entityType = _.get(this.props, 'query.entityType')

    if (prevEntityType !== entityType) {
      await this.resolveEntitySchema()
    }

    const prevEntityId = _.get(prevProps.value ?? prevProps.defaultValue, 'entityId')
    const entityId = _.get(this.props.value ?? this.props.defaultValue, 'entityId')

    if (prevEntityId !== entityId) {
      if (_.isNil(entityId)) {
        this.setState({ entity: undefined })
      } else {
        await this.fetchValueEntity(entityId)
      }
    }

    if (this.props.searchMode === 'submit') {
      const currHasTypedValue = !_.isEmpty(_.trim(this.state.submitSearchFilter)) && !this.state.isCurrentFilterCommitted
      if (!currHasTypedValue) {
        if (this.submitButtonShowTimer) {
          clearTimeout(this.submitButtonShowTimer)
          this.submitButtonShowTimer = null
        }
        if (this.state.isSubmitButtonVisible) {
          this.setState({ isSubmitButtonVisible: false })
        }
      } else if (this.state.submitSearchFilter !== prevState.submitSearchFilter) {
        if (this.submitButtonShowTimer) {
          clearTimeout(this.submitButtonShowTimer)
        }
        this.submitButtonShowTimer = setTimeout(() => {
          this.submitButtonShowTimer = null
          this.setState({ isSubmitButtonVisible: true })
        }, 400)
      }
    }
  }

  public componentWillUnmount() {
    if (this.submitButtonShowTimer) {
      clearTimeout(this.submitButtonShowTimer)
    }
  }

  public render() {
    const { className, style } = this.props

    return (
      <div className={classNames('entity-search', className)} style={style}>
        {this.renderMainControl()}
      </div>
    )
  }

  private renderMainControl = () => {
    const { searchMode = 'auto' } = this.props
    const { entity } = this.state
    const schemaPaths = this.getSchemaPaths()

    if (entity && searchMode === 'submit') {
      return this.renderSubmitSelectedEntityCard(schemaPaths.inlinePreview)
    }

    if (entity) {
      return this.renderSelectedEntityCard(schemaPaths.inlinePreview)
    }

    if (searchMode === 'submit') {
      return this.renderSubmitSearchField()
    }

    return this.renderTouchableField()
  }

  private renderSubmitSearchField = () => {
    const { isEditable, isDisabled, isReadOnly, isHorizontalLayout, placeholder, frames, label } =
      this.props
    const { submitSearchFilter, submitRequestedSearchFilter, isSubmitResultsOpen, isSubmitButtonVisible } = this.state

    const isControlDisabled = Boolean(isDisabled)
    const isControlReadOnly = Boolean(isReadOnly || !isEditable)
    const canSubmitSearch = Boolean(
      isEditable && !isDisabled && !isReadOnly && this.isSubmitSearchValid(submitSearchFilter)
    )
    const hasUnsubmittedValue = isSubmitButtonVisible && canSubmitSearch && !this.state.isCurrentFilterCommitted && !isSubmitResultsOpen
    const hasTypedValue = !_.isEmpty(_.trim(submitSearchFilter)) && !this.state.isCurrentFilterCommitted

    const schemaPaths = this.getSchemaPaths()

    return (
      <div className="entity-search__submit-search-row">
        <div className="entity-search__submit-search-input">
          <MobileInputField
            className="entity-search__touchable-field"
            frames={frames}
            value={submitSearchFilter}
            onChange={this.handleSubmitSearchFilterChange}
            onKeyPress={this.handleSubmitSearchKeyPress}
            autoFocus={this.props.autoFocus}
            isHorizontalLayout={isHorizontalLayout}
            isDisabled={isControlDisabled}
            isReadOnly={Boolean(isReadOnly)}
            readOnly={isControlReadOnly}
            inputType="search"
            placeholder={placeholder || this.getPlaceholder()}
            inputClassName={classNames(
              'mobile-control',
              'entity-search__touchable-input',
              { 'entity-search__submit-search-input-control': isSubmitButtonVisible }
            )}
            inputStyle={hasTypedValue ? { color: '#5c7080', fontStyle: 'italic' } : undefined}
          />

          {isSubmitButtonVisible && (
            <TetherTarget
              ref={this.handleSubmitResultsTetherRef}
              openOnClick={canSubmitSearch}
              closeOnEsc={true}
              closeOnOutsideClick={true}
              closeOnPortalClick={false}
              closeOnTetherTargetClick={false}
              automaticAdjustOffset={true}
              onClose={this.handleSubmitResultsClosed}
              tetherOptions={{
                attachment: 'top right',
                targetAttachment: 'bottom right',
                offset: {
                  top: 8,
                  left: 0,
                },
              }}
              tethered={
                <MobileEntitySearchFieldSubmitResultsOverlay
                  fieldProps={this.props}
                  componentsContext={this.context}
                  entitySchema={this.entitySchema}
                  entityDisplayName={this.entityDisplayName}
                  schemaPaths={schemaPaths}
                  searchFilter={submitRequestedSearchFilter}
                  onCommitSelection={this.handleCommitSelection}
                  onCommitRawValue={this.handleCommitRawValue}
                  onClose={this.closeSubmitResults}
                  onSubmitSearch={this.handleSubmitSearch}
                />
              }
            >
              <Button
                className={classNames('entity-search__submit-search-button', {
                  'entity-search__submit-search-button--pulse': hasUnsubmittedValue,
                  'entity-search__submit-search-button--results-open': isSubmitResultsOpen || !canSubmitSearch,
                })}
                onMouseDown={(!isSubmitResultsOpen && canSubmitSearch) ? this.handleSubmitSearchPress : undefined}
                onClick={_.noop}
              >
                <Icon icon={IconNames.ARROW_RIGHT} />
              </Button>
            </TetherTarget>
          )}
        </div>
      </div>
    )
  }

  private renderSubmitSelectedEntityCard = (inlinePreviewSchemaPath?: string) => {
    return this.renderSelectedEntityCard(inlinePreviewSchemaPath, {
      forceInlineClearButton: true,
      isCardClickable: false,
      showChevron: false,
    })
  }

  private renderTouchableField = () => {
    const {
      isEditable,
      isDisabled,
      isReadOnly,
      isHorizontalLayout,
      label,
      placeholder,
      errors,
      frames,
      optionValuePath = 'displayName',
    } = this.props
    const { entity } = this.state

    const displayValue = entity ? _.get(entity, optionValuePath) : undefined
    const isControlDisabled = Boolean(isDisabled)
    const isControlReadOnly = Boolean(isReadOnly || !isEditable)
    const isControlInteractive = Boolean(isEditable && !isDisabled && !isReadOnly)
    const TouchableInputField: any = MobileInputField

    return (
      <TouchableInputField
        className={classNames('entity-search__touchable-field', {
          'entity-search__touchable-field--interactive': isControlInteractive,
        })}
        frames={frames}
        value={displayValue ?? ''}
        errors={errors}
        onChange={_.noop}
        onClick={isControlInteractive ? this.handleSearchPress : undefined}
        isHorizontalLayout={isHorizontalLayout}
        isDisabled={isControlDisabled}
        isReadOnly={Boolean(isReadOnly)}
        readOnly={isControlReadOnly}
        placeholder={placeholder || this.getPlaceholder()}
        rightIcon={IconNames.CHEVRON_RIGHT}
        inputClassName={classNames(
          'mobile-control',
          'entity-search__touchable-input',
          {
            'entity-search__touchable-input--interactive': isControlInteractive,
          }
        )}
      />
    )
  }

  private renderSelectedEntityCard = (
    inlinePreviewSchemaPath?: string,
    options: {
      forceInlineClearButton?: boolean
      isCardClickable?: boolean
      showChevron?: boolean
    } = {}
  ) => {
    const { errors, isEditable, isDisabled, isReadOnly, isClearable } = this.props
    const { entity } = this.state
    const { forceInlineClearButton = false, isCardClickable = true, showChevron = true } = options

    const canInteract = Boolean(isEditable && !isDisabled && !isReadOnly)
    const shouldRenderInlineClearButton = Boolean(
      forceInlineClearButton || (isClearable && canInteract)
    )

    const hasError = !_.isEmpty(errors)

    return (
      <div
        className={classNames('entity-search__selected-card', {
          'entity-search__selected-card--error': hasError,
        })}
      >
        <div
          onClick={isCardClickable && canInteract ? this.handleSearchPress : undefined}
          className={classNames('entity-search__selected-card-body', {
            pointer: isCardClickable && canInteract,
          })}
        >
          <div className="entity-search__selected-card-content">
            {inlinePreviewSchemaPath && entity ? (
              <MultiMixinRenderer
                actions={this}
                componentsMap={MobileComponents}
                context={{
                  density: 'collapse',
                  isHorizontalLayout: true,
                  isStatic: true,
                }}
                // Omit ambient state keys that collide with the entity's own top-level fields.
                state={_.omit(
                  { ...this.context, ...this.props, entity },
                  Object.keys((entity as any)?.content ?? {})
                )}
                value={entity}
                uiSchemaPath={inlinePreviewSchemaPath}
              />
            ) : (
              <MobileEntityPreviewCard entity={entity} />
            )}
          </div>
          {showChevron ? <Icon icon={IconNames.CHEVRON_RIGHT} className="entity-search__chevron mr1" /> : null}
        </div>
        {shouldRenderInlineClearButton ? this.renderInlineClearButton() : null}
      </div>
    )
  }

  private renderInlineClearButton = () => {
    return (
      <div className="entity-search__actions">
        <Button
          className={classNames(Classes.MINIMAL, 'entity-search__actions-trigger')}
          onClick={this.handleMenuClearPress}
          data-debug-id="mobile-entity-search-field-clear"
        >
          <Icon
            icon={IconNames.CROSS}
            className="entity-search__actions-trigger-icon"
          />
        </Button>
      </div>
    )
  }

  private handleSubmitResultsTetherRef = (ref: any) => {
    this.submitResultsTetherRef = ref
  }

  private handleSearchPress = () => {
    this.openOverlay()
  }

  private handleMenuClearPress = () => {
    const { onChange, onEntityChange, rawQueryPath } = this.props
    onChange?.({})
    onEntityChange?.({ entityId: undefined, entity: undefined })
    if (rawQueryPath) {
      (onChange as any)?.(undefined, false, rawQueryPath)
    }
    this.setState({ entity: undefined, submitSearchFilter: '', submitRequestedSearchFilter: '', isCurrentFilterCommitted: false, submitNoResultsCount: 0 })
  }

  private handleSubmitSearchFilterChange = (value: string) => {
    const { rawQueryPath, onChange, frames } = this.props
    if (rawQueryPath && !_.isEmpty(frames?.getValue?.(rawQueryPath))) {
      (onChange as any)?.(null, false, rawQueryPath)
    }
    this.closeSubmitResults()
    this.setState({ submitSearchFilter: value, isCurrentFilterCommitted: false, isSubmitResultsOpen: false })
  }

  private handleSubmitSearchPress = () => {
    const { submitSearchFilter } = this.state
    const trimmedSearchFilter = _.trim(submitSearchFilter)

    if (!this.isSubmitSearchValid(trimmedSearchFilter)) {
      return
    }

    this.setState({ submitRequestedSearchFilter: trimmedSearchFilter, isSubmitResultsOpen: true })
    this.submitResultsTetherRef?.open?.()
  }

  private handleSubmitResultsClosed = () => {
    this.setState({ isSubmitResultsOpen: false })
  }

  private handleCommitRawValue = (searchFilter: string) => {
    const { rawQueryPath, onChange } = this.props
    if (rawQueryPath) {
      (onChange as any)?.(searchFilter || null, false, rawQueryPath)
    }
    this.setState({ isCurrentFilterCommitted: true })
    this.closeSubmitResults()
  }

  private handleSubmitSearchKeyPress = (event?: any) => {
    if (!this.isSubmitKeyboardAction(event)) {
      return
    }

    event?.preventDefault?.()
    this.handleSubmitSearchPress()
  }

  private isSubmitKeyboardAction = (event?: any) => {
    const normalizedKey = _.toLower(
      event?.key || event?.code || event?.nativeEvent?.key || event?.nativeEvent?.code || ''
    )
    const keyCode =
      event?.which || event?.keyCode || event?.nativeEvent?.which || event?.nativeEvent?.keyCode

    return (
      normalizedKey === 'enter' ||
      normalizedKey === 'numpadenter' ||
      normalizedKey === 'go' ||
      normalizedKey === 'search' ||
      normalizedKey === 'done' ||
      normalizedKey === 'send' ||
      keyCode === 13
    )
  }

  private closeSubmitResults = () => {
    this.submitResultsTetherRef?.close?.()
  }

  private handleSubmitSearch = (hasResults: boolean) => {
    const { onSubmitSearch } = this.props
    this.setState((prevState) => {
      const numberOfAttempts = hasResults ? 0 : prevState.submitNoResultsCount + 1
      onSubmitSearch?.({ hasResults, numberOfAttempts })
      return { submitNoResultsCount: numberOfAttempts }
    })
  }

  private openOverlay = () => {
    const schemaPaths = this.getSchemaPaths()

    const ModalContent = (injectedProps: any) => (
      <MobileEntitySearchFieldOverlay
        fieldProps={this.props}
        componentsContext={this.context}
        entitySchema={this.entitySchema}
        entityDisplayName={this.entityDisplayName}
        entityDisplayNamePlural={this.entityDisplayNamePlural}
        schemaPaths={schemaPaths}
        onCommitSelection={this.handleCommitSelection}
        onClose={injectedProps.onClose}
      />
    )

    MobileModal.open(<ModalContent />, {
      modalDialogClassName: 'overflow-hidden',
    })
  }

  private handleCommitSelection = (payload: ISelectionPayload) => {
    const { entity, searchFilter } = payload
    const { valueMappings = [], frames, onChange, onEntityChange, rawQueryPath } = this.props

    this.setState({ entity, isCurrentFilterCommitted: true })

    const mappedData =
      evaluateMappings(valueMappings, frames, {
        item: entity,
        entity,
        searchFilter,
        isCreateEntitySelected: false,
        ...entity,
      }) ?? {}

    onChange?.({
      entityId: entity?.uniqueId,
      displayName: entity?.displayName,
      ...mappedData,
    })

    onEntityChange?.({ entityId: entity?.uniqueId, entity })

    if (rawQueryPath) {
      (onChange as any)?.(entity?.displayName || searchFilter, false, rawQueryPath)
    }
  }

  private resolveEntitySchema = async () => {
    const entityType = this.getQueryEntityType()

    if (!entityType) {
      this.entitySchema = {}
      this.entityDisplayName = DEFAULT_ENTITY_TYPE_NAME
      return
    }

    this.entitySchema = await this.store.resolveSchema(entityType)
    this.entityDisplayName =
      this.props?.query?.entityDisplayName ??
      this.props?.query?.itemName ??
      this.entitySchema?.metadata?.entityTypeDisplayName ??
      DEFAULT_ENTITY_TYPE_NAME
    this.entityDisplayNamePlural = translateString(
      this.entityDisplayName,
      this.entitySchema?.translations ?? {},
      { count: 'many' }
    )
  }

  private fetchValueEntity = async (uniqueId: string) => {
    this.setState({ isLoading: true })

    try {
      const entity = await this.store.findRecord(uniqueId)
      this.setState({ entity, isLoading: false })
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  private getQueryEntityType = () => {
    return this.props?.query?.entityType
  }

  private isSubmitSearchValid = (searchFilter: string) => {
    const { minQueryLength } = this.props
    const trimmedSearchFilter = _.trim(searchFilter)
    const minimumLength = Math.max(1, Number(minQueryLength) || 0)

    return !_.isEmpty(trimmedSearchFilter) && trimmedSearchFilter.length >= minimumLength
  }

  private getPlaceholder = () => {
    return translateString('Entity_Search_Placeholder', globalTranslationTable, {
      entityDisplayNamePlural: _.lowerCase(this.entityDisplayNamePlural),
    })
  }

  private getSchemaPaths = (): IResolvedSchemaPaths => {
    return {
      item: _.get(this.entitySchema, this.props.itemSchemaPath || '') ? this.props.itemSchemaPath : undefined,
      help: _.get(this.entitySchema, this.props.helpSchemaPath || '') ? this.props.helpSchemaPath : undefined,
      emptyResult: _.get(this.entitySchema, this.props.emptyResultsSchemaPath || '') ? this.props.emptyResultsSchemaPath : undefined,
      inlinePreview: _.get(this.entitySchema, this.props.inlinePreviewSchemaPath || '') ? this.props.inlinePreviewSchemaPath : undefined,
    }
  }
}

export const MobileEntitySearchField = LabelFieldFactory({ InputComponent: MobileEntitySearchFieldImpl })
